import React, { useEffect, useRef, useState } from 'react';
import {
  IQRCodeContentCommonProps,
  QRCodeContentNavigationActions,
  QRCodeScreenTypes,
} from '../../QRCodeCommonComponentEnums';
import QRCodeContentViewWrapper from '../QRCodeViewWrapper';
import { useTranslation } from 'react-i18next';
import DeliveryDashboardBottomSheet from '@src/pages/DeliveryDashboard/components/DeliveryDashboardBottomSheet';
import styles from './QRCodeDownloadContent.module.scss';
import PIEText from '@src/shared/components/PIEText';
import {
  IconCheckCircle,
  IconClose,
  IconCloseCircle,
  IconDownload,
  IconHelmet,
  IconHelpCircle,
  IconImage,
  IconInfoCircle,
} from '@justeattakeaway/pie-icons-react';
import PIEModal from '@src/shared/components/PIEModal';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIEButton from '@src/shared/components/PIEButton';
import TakeawayLogo from '@src/assets/tkwylogo.svg';
import QRCode from 'react-qr-code';
import QRCodeDownloadPrintContent from './components/QRCodeDownloadPrintContent';
import to from 'await-to-js';
import html2topdf from 'html2pdf.js';
import PIEToast from '@src/shared/components/PIEToast';
import dayjs from 'dayjs';
interface componentProps extends IQRCodeContentCommonProps {
  qrCode: string;
  connectionCode: string;
  connectionCodeVersion: number;
  lastSeenVersion: number;
  onVersionAcknowledged: () => void;
}

const QRCodeDownloadContent = ({
  bottomSheetView,
  popupView,
  onNavigate,
  qrCode,
  connectionCode,
  connectionCodeVersion,
  lastSeenVersion,
  onVersionAcknowledged,
}: componentProps) => {
  const { t } = useTranslation();
  const qrCodeRef = React.useRef<HTMLDivElement>(null);
  const [showToastMessage, setShowToastMessage] = useState(false);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showConnectionCodeRefreshedToast, setShowConnectionCodeRefreshedToast] = useState(false);
  const hasAcknowledgedVersion = useRef<boolean>(false);

  useEffect(() => {
    // Show toast if version changed since last time this screen was viewed
    if (connectionCodeVersion > lastSeenVersion && !hasAcknowledgedVersion.current) {
      setShowConnectionCodeRefreshedToast(false);
      setShowToastMessage(false);
      requestAnimationFrame(() => {
        setShowConnectionCodeRefreshedToast(true);
      });
      hasAcknowledgedVersion.current = true;
      onVersionAcknowledged();
    }
  }, [connectionCodeVersion, lastSeenVersion, onVersionAcknowledged]);

  // Reset acknowledged flag when version changes while on this screen
  useEffect(() => {
    hasAcknowledgedVersion.current = false;
  }, [connectionCodeVersion]);

  const onCloseHandler = () => {
    onNavigate?.(QRCodeContentNavigationActions.CLOSE);
  };
  const [isComponentBusy, setIsComponentBusy] = useState(false);

  const handleDownload = async () => {
    if (isComponentBusy) {
      return;
    }
    setShowToastMessage(false);
    if (qrCodeRef.current === null) {
      return;
    }
    const qrCodeElement = qrCodeRef.current;
    setIsComponentBusy(true);
    const [err] = await to(
      html2topdf(qrCodeElement, {
        filename: `courier-connectioncodes-${dayjs().format('YYYY-MM-DD__HH.mm')}.pdf`,
        image: { type: 'jpeg', quality: 1 },
      })
    );
    if (!err) {
      setToastType('success');
    } else {
      setToastType('error');
    }
    setShowToastMessage(true);
    setIsComponentBusy(false);
  };
  const renderContent = () => {
    return (
      <div className={styles.content}>
        {showConnectionCodeRefreshedToast && (
          <PIEToast
            isOpen={true}
            mode="regular"
            align="b-c"
            modCompact
            autoCloseTimeout={4000}
            icon={<></>}
            qaSelector="connection-code-refreshed-message"
            primaryActionButtonProps={{
              children: <IconClose size="s" />,
              onClick: () => {
                setShowConnectionCodeRefreshedToast(false);
              },
            }}
          >
            <div style={{ display: 'flex' }}>
              <PIEComponentUtil marginRight="var(--xds-spacing-a)">
                <IconInfoCircle size="s" color="var(--xds-color-support-info-inverse)" />
              </PIEComponentUtil>
              <div>
                <PIEText
                  size="var(--xds-font-size-13)"
                  fontWeight="var(--xds-font-weight-extrabold)"
                  text={t('ocf_screens.QR_CODE_GENERATED_SUCCESS_MESSAGE')}
                />

                <PIEText
                  size="var(--xds-font-size-14)"
                  fontWeight="var(--xds-font-weight-regular)"
                  text={t('ocf_screens.QR_CODE_DOWNLOAD_UPDATED_POSTER')}
                />
              </div>
            </div>
          </PIEToast>
        )}
        {showToastMessage && !showConnectionCodeRefreshedToast && (
          <PIEToast
            isOpen={true}
            mode="regular"
            align="t-c"
            modCompact
            autoCloseTimeout={2000}
            icon={<></>}
            qaSelector="download-status-message"
          >
            <div style={{ display: 'flex' }}>
              <PIEComponentUtil marginRight="var(--xds-spacing-a)">
                {(toastType === 'success' && (
                  <IconCheckCircle size="s" color="var(--xds-color-support-positive-inverse)" />
                )) || <IconCloseCircle size="s" color="red" />}
              </PIEComponentUtil>
              {toastType === 'success' ? (
                <div>{t('ocf_screens.QR_CODE_DOWNLOAD_SUCCESS_MESSAGE')}</div>
              ) : (
                <div>{t('ocf_screens.QR_CODE_DOWNLOAD_ERROR_MESSAGE')}</div>
              )}
            </div>
          </PIEToast>
        )}
        <PIEComponentUtil paddingX="var(--xds-spacing-c)">
          <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
            <PIEText
              size={'var(--xds-font-size-14)'}
              fontWeight="var(--xds-font-weight-regular)"
              text={
                <>
                  <div className={styles.informationContainer}>
                    <IconInfoCircle size="xs" /> {t('ocf_screens.QR_CODE_DOWNLOAD_INFO_MESSAGE')}
                  </div>
                </>
              }
            />
          </PIEComponentUtil>
          <div className={styles.previewContainer}>
            <div className={styles.tagDescription}>
              <IconImage size="m" className={styles.tagDescriptionIcon} color="#fff" />
              <span className={styles.tagDescriptionText}>
                {t('ocf_screens.QR_CODE_POSTER_PREVIEW')}{' '}
              </span>
            </div>

            <div className={styles.preview}>
              <img src={TakeawayLogo} />
              <PIEComponentUtil marginY="var(--xds-spacing-b)">
                <PIEText
                  size={'var(--xds-font-size-14)'}
                  fontWeight="var(--xds-font-weight-bold)"
                  text={t('ocf_screens.QR_CODE_DOWNLOAD_POSTER_TITLE')}
                  align="center"
                />
              </PIEComponentUtil>

              <PIEComponentUtil marginBottom="var(--xds-spacing-b)">
                <PIEText
                  size={'var(--xds-font-size-12)'}
                  fontWeight="var(--xds-font-weight-regular)"
                  text={
                    <div style={{ width: '300px' }}>
                      <span className={styles.chip}>1</span>{' '}
                      {t('ocf_screens.QR_CODE_DOWNLOAD_STEP_1_CONTENT')}
                    </div>
                  }
                  align="center"
                />
              </PIEComponentUtil>
              <PIEComponentUtil marginBottom="var(--xds-spacing-b)" marginLeft="-2px">
                <PIEText
                  size={'var(--xds-font-size-12)'}
                  fontWeight="var(--xds-font-weight-regular)"
                  text={
                    <div style={{ width: '300px' }}>
                      <span className={styles.chip}>2</span>{' '}
                      {t('ocf_screens.QR_CODE_DOWNLOAD_STEP_2_CONTENT')}
                    </div>
                  }
                  align="center"
                />
              </PIEComponentUtil>
              <div className={styles.qrCodeContainer}>
                <QRCode value={qrCode} size={80}></QRCode>
              </div>
              <PIEComponentUtil marginTop="var(--xds-spacing-b)">
                <div className={styles.bottomTag}>
                  <IconHelmet size="xs" className={styles.tagIcon} />
                  {t('ocf_screens.QR_CODE_DOWNLOAD_FOOTER_TAG')}
                </div>
              </PIEComponentUtil>
            </div>
          </div>
          <PIEComponentUtil marginBottom="var(--xds-spacing-e)">
            <PIEText
              size={'var(--xds-font-size-16)'}
              fontWeight="var(--xds-font-weight-bold)"
              align="center"
              text={
                <div
                  className={styles.helpContainer}
                  onClick={() => {
                    onNavigate?.(
                      QRCodeContentNavigationActions.JUMP_SCREEN,
                      QRCodeScreenTypes.QR_CODE_FAQ
                    );
                  }}
                >
                  <IconHelpCircle size="s" />
                  <u>{t('ocf_screens.QR_CODE_HELP_LINK')}</u>
                </div>
              }
            />
          </PIEComponentUtil>

          <PIEButton
            icon={!isComponentBusy ? <IconDownload size="m" /> : undefined}
            autoWidth
            size="medium"
            mode="primary"
            type="button"
            text={
              !isComponentBusy
                ? t('ocf_screens.QR_CODE_DOWNLOAD_BUTTON')
                : t('ocf_screens.QR_CODE_DOWNLOAD_BUTTON_BUSY')
            }
            onClick={() => handleDownload()}
          ></PIEButton>
        </PIEComponentUtil>

        <div className={styles.real_printed_content}>
          <div className={styles.actualContent} ref={qrCodeRef}>
            {/* REAL PRINTED CONTENT */}
            <QRCodeDownloadPrintContent logo={TakeawayLogo} qrCode={qrCode} />
            {/* /REAL PRINTED CONTENT */}
          </div>
        </div>
      </div>
    );
  };

  const renderDesktopView = (content: React.ReactNode) => {
    return (
      <PIEModal isOpen={true} size="small" modOverlay>
        <PIEModal.Header
          onClose={onCloseHandler}
          heading={
            <PIEText
              size={'var(--xds-font-size-24)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_DOWNLOAD_TITLE')}
            />
          }
        />
        <PIEModal.Content>{content}</PIEModal.Content>
      </PIEModal>
    );
  };

  const renderMobileView = (
    content: React.ReactNode,
    overrideBlockingValue?: boolean | undefined
  ) => {
    return (
      <DeliveryDashboardBottomSheet
        blocking={overrideBlockingValue !== undefined ? overrideBlockingValue : true}
        constantHeight={640}
        onBottomSheetHeight={() => {}}
        showCloseButton
        onClose={onCloseHandler}
        header={
          <div className={styles.mobileBottomSheetHeader}>
            <PIEText
              align="left"
              size={'var(--xds-font-size-20)'}
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.QR_CODE_DOWNLOAD_TITLE')}
            />
          </div>
        }
      >
        <PIEComponentUtil padding="var(--xds-spacing-d)">{content}</PIEComponentUtil>
      </DeliveryDashboardBottomSheet>
    );
  };

  return (
    <QRCodeContentViewWrapper
      content={renderContent()}
      bottomSheetView={bottomSheetView}
      popupView={popupView}
      bottomSheetWrapper={renderMobileView}
      popupWrapper={renderDesktopView}
    />
  );
};

export default QRCodeDownloadContent;
